import { createPortal } from "react-dom";
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleDashed,
  Loader2,
  Minus,
  X,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PeakRss } from "@/components/MemoryPanel";
import { CacheChip } from "@/components/CacheChip";
import { FailureChip } from "@/components/FailureChip";
import { useLiveJobs } from "@/components/registry/useLiveJobs";
import { openBenchDiagnostics } from "@/components/registry/BenchDiagnostics";
import { offersDiagnostics } from "@/lib/benchDiagnostics";
import {
  chronology,
  fmtClock,
  fmtMs,
  publishStepsView,
  type AttemptInput,
  type BuildRef,
  type PublishStepRow,
  type StepState,
} from "@/lib/publishSteps";
import { registryPublicUrl } from "@/lib/registryBuilds";
import {
  countStates,
  filterRows,
  flattenRows,
  neighborStep,
  stepSummary,
} from "@/lib/stepPanel";
import {
  useIsDesktop,
  useStepSelection,
} from "@/components/registry/useStepSelection";
import { cn } from "@/lib/utils";

export const PILL: Record<StepState, string> = {
  pending: "border-border text-muted-foreground bg-transparent",
  running: "border-blue-500 bg-blue-500/10 text-blue-700 dark:text-blue-300",
  ok: "border-green-600/50 bg-green-600/10 text-green-700 dark:text-green-400",
  degraded:
    "border-yellow-500/60 bg-yellow-500/15 text-yellow-800 dark:text-yellow-300",
  failed: "border-destructive bg-destructive/10 text-destructive",
  skipped: "border-border/60 bg-muted text-muted-foreground",
};

function allBuilds(rows: readonly PublishStepRow[]): BuildRef[] {
  return rows.flatMap((r) => [...r.builds, ...allBuilds(r.subrows)]);
}

function BuildLine({
  b,
  job,
}: {
  b: BuildRef;
  job: ReturnType<ReturnType<typeof useLiveJobs>["get"]>;
}) {
  const status = (job?.status ?? b.status) as string;
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
      {b.seqText && (
        <span className="tabular-nums text-muted-foreground">{b.seqText}</span>
      )}
      <span className="font-medium">{b.label}</span>
      {b.local ? (
        <Badge variant="outline" className="font-normal">
          local run
        </Badge>
      ) : (
        <Badge variant="outline" className="font-normal">
          runner build
        </Badge>
      )}
      {b.jobId && (
        <Link
          to={`/logs/${encodeURIComponent(b.jobId)}`}
          className="font-mono underline decoration-dotted hover:text-foreground"
          title={`Open job ${b.jobId} (build log, flags, runner journal)`}
        >
          job {b.jobId.slice(0, 8)}
        </Link>
      )}
      {b.jobId && !b.local && (
        <>
          <PeakRss job={{ ...(job ?? {}), status }} />
          <CacheChip job={{ ...(job ?? {}), status }} />
        </>
      )}
      {job?.failure_class && (
        <FailureChip job={{ ...job, status } as never} className="basis-full" />
      )}
    </li>
  );
}

export const STATE_ICON: Record<StepState, LucideIcon> = {
  pending: CircleDashed,
  running: Loader2,
  ok: Check,
  degraded: AlertTriangle,
  failed: XCircle,
  skipped: Minus,
};

function StatePill({ r }: { r: PublishStepRow }) {
  const Icon = STATE_ICON[r.state];
  return (
    <span
      className={cn(
        "inline-flex min-w-[5.5rem] shrink-0 items-center justify-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold",
        PILL[r.state],
      )}
      data-testid="step-pill"
    >
      <Icon
        className={cn("h-3 w-3", r.state === "running" && "animate-spin")}
        aria-hidden
      />
      {r.stateLabel}
    </span>
  );
}

function durationText(r: PublishStepRow): string {
  if (r.durationMs !== null) return fmtMs(r.durationMs);
  if (r.state === "running" && r.startedAt !== null)
    return `${fmtMs(Date.now() - r.startedAt)}…`;
  return "";
}

/** A collapsed step: one dense, keyboard-operable row. It never expands
 *  inline; activating it opens the detail panel. */
function StepRow({
  r,
  selected,
  onOpen,
  nested = false,
}: {
  r: PublishStepRow;
  selected: boolean;
  onOpen: (n: string) => void;
  nested?: boolean;
}) {
  const dim = r.state === "pending" || r.state === "skipped";
  const dur = durationText(r);
  return (
    <li
      data-step={r.n}
      data-state={r.state}
      className={cn(
        "rounded-md border text-xs",
        nested && "ml-3 sm:ml-6",
        r.state === "failed" && "border-destructive/60",
        r.state === "degraded" && "border-yellow-500/60",
        selected && "ring-2 ring-ring",
        dim && "opacity-70",
      )}
    >
      <button
        type="button"
        onClick={() => onOpen(r.n)}
        aria-haspopup="dialog"
        aria-expanded={selected}
        aria-label={`Step ${r.n}: ${r.title}, ${r.stateLabel}. Open details`}
        className="flex w-full items-center gap-x-2 rounded-md px-2 py-1.5 text-left hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="w-8 shrink-0 font-mono tabular-nums text-muted-foreground">
          {r.n}
        </span>
        <StatePill r={r} />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-medium">{r.title}</span>
            {r.badges.map((b) => (
              <span
                key={b}
                className="font-mono text-[10px] text-muted-foreground"
              >
                {b}
              </span>
            ))}
          </span>
          <span
            className={cn(
              "block truncate",
              r.state === "failed"
                ? "text-destructive"
                : r.state === "degraded"
                  ? "text-yellow-800 dark:text-yellow-300"
                  : "text-muted-foreground",
            )}
            data-testid="step-summary"
          >
            {stepSummary(r)}
          </span>
        </span>
        <span className="w-16 shrink-0 text-right tabular-nums text-muted-foreground">
          {dur}
        </span>
        <ChevronRight
          className="h-4 w-4 shrink-0 text-muted-foreground"
          aria-hidden
        />
      </button>
    </li>
  );
}

/** The full detail of one step, exactly the content the card used to show
 *  inline (timing, progress, builds, reason, details, views, build logs),
 *  plus its sub-rows as links to their own panels. */
function StepDetail({
  r,
  jobs,
  registryUrl,
  onOpen,
  onViewDiagnostics,
}: {
  r: PublishStepRow;
  jobs: ReturnType<typeof useLiveJobs>;
  registryUrl: string;
  onOpen: (n: string) => void;
  /** Present when the step panel can jump to the attempt's diagnostics. */
  onViewDiagnostics?: () => void;
}) {
  return (
    <div className="space-y-3 text-xs">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <StatePill r={r} />
        {r.badges.map((b) => (
          <Badge key={b} variant="outline" className="font-mono font-normal">
            {b}
          </Badge>
        ))}
        {r.runnerBuilds > 0 && (
          <span className="text-muted-foreground">
            {r.runnerBuilds} runner build{r.runnerBuilds === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-muted-foreground">
        <span>
          start{" "}
          <span className="tabular-nums text-foreground">
            {fmtClock(r.startedAt)}
          </span>
        </span>
        <span>
          end{" "}
          <span className="tabular-nums text-foreground">
            {fmtClock(r.endedAt)}
          </span>
        </span>
        <span>
          took{" "}
          <span className="tabular-nums text-foreground">
            {r.durationMs !== null
              ? fmtMs(r.durationMs)
              : r.state === "running" && r.startedAt !== null
                ? `${fmtMs(Date.now() - r.startedAt)} so far`
                : "—"}
          </span>
        </span>
        <span className="col-span-2" title={r.what}>
          ran on <span className="text-foreground">{r.where}</span>
        </span>
      </div>
      {r.progress && (
        <div
          className="font-mono text-blue-700 dark:text-blue-300"
          data-testid="step-progress"
        >
          {r.progress}
        </div>
      )}
      {r.builds.length > 0 && (
        <ul className="space-y-1">
          {r.builds.map((b, i) => (
            <BuildLine
              key={`${b.seqText}${b.jobId}${i}`}
              b={b}
              job={b.jobId ? jobs.get(b.jobId) : undefined}
            />
          ))}
        </ul>
      )}
      {r.reason && (
        <pre
          className={cn(
            "whitespace-pre-wrap break-words font-mono text-[11px]",
            r.state === "failed" || r.state === "degraded"
              ? "text-foreground"
              : "text-muted-foreground",
          )}
          data-testid="step-reason"
        >
          {r.reason}
        </pre>
      )}
      {onViewDiagnostics && offersDiagnostics(r.key, r.state) && (
        <Button
          variant="outline"
          size="sm"
          data-testid="view-diagnostics"
          title="Open the attempt's Benchmark diagnostics: every raw error, exit code and stderr line (internal only)"
          onClick={onViewDiagnostics}
        >
          View diagnostics
        </Button>
      )}
      <div className="space-y-1">
        <div className="text-muted-foreground">{r.what}</div>
        {r.details.map((d) => (
          <div key={d.label} className="flex flex-wrap gap-x-2">
            <span className="text-muted-foreground">{d.label}:</span>
            <span className="break-all font-mono">{d.value}</span>
          </div>
        ))}
        {r.linkPath && (
          <a
            href={`${registryUrl}${r.linkPath}`}
            target="_blank"
            rel="noreferrer"
            className="font-mono underline decoration-dotted hover:text-foreground"
          >
            {r.linkPath}
          </a>
        )}
        {r.views.length > 0 && (
          <ul className="space-y-1" data-testid="step-views">
            {r.views.map((v, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-x-2">
                <Badge
                  variant={
                    v.state === "ok"
                      ? "success"
                      : v.state === "partial"
                        ? "warning"
                        : "destructive"
                  }
                >
                  {v.state === "ok"
                    ? "measured"
                    : v.state === "partial"
                      ? "partial"
                      : "not measured"}
                </Badge>
                <span className="font-medium">{v.title}</span>
                {v.headline && (
                  <span className="tabular-nums">{v.headline}</span>
                )}
                {v.reason && (
                  <span className="break-words font-mono text-muted-foreground">
                    {v.reason}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {r.builds
          .filter((b) => b.jobId)
          .map((b) => (
            <div key={b.jobId} className="flex flex-wrap gap-x-3">
              <Link
                to={`/logs/${encodeURIComponent(b.jobId!)}`}
                className="underline decoration-dotted hover:text-foreground"
              >
                build log · {b.label}
              </Link>
              {jobs.get(b.jobId!)?.bundle_key && (
                <Link
                  to={`/logs/${encodeURIComponent(b.jobId!)}#failure`}
                  className="underline decoration-dotted text-destructive hover:text-foreground"
                >
                  failure bundle
                </Link>
              )}
            </div>
          ))}
      </div>
      {r.subrows.length > 0 && (
        <div className="space-y-1">
          <div className="font-medium">Sub-steps</div>
          <ul className="space-y-1.5">
            {r.subrows.map((s) => (
              <StepRow key={s.n} r={s} selected={false} onOpen={onOpen} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input,select,textarea,summary,[tabindex]:not([tabindex="-1"])';

/** Right-hand side panel (>= 768px, overlays, no backdrop, the page keeps its
 *  scroll) or a full-height bottom sheet with a backdrop (phones). Esc closes,
 *  Tab is trapped, focus returns to the row, J/K or the arrow buttons move
 *  between steps. */
function StepPanel({
  r,
  order,
  desktop,
  jobs,
  registryUrl,
  onSelect,
  onClose,
  attemptKey,
}: {
  r: PublishStepRow;
  order: string[];
  desktop: boolean;
  jobs: ReturnType<typeof useLiveJobs>;
  registryUrl: string;
  onSelect: (n: string) => void;
  onClose: () => void;
  attemptKey: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const prev = neighborStep(order, r.n, -1);
  const next = neighborStep(order, r.n, 1);
  const idx = order.indexOf(r.n);

  useEffect(() => {
    returnTo.current = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const restore = returnTo.current;
    return () => {
      if (restore && restore.isConnected)
        restore.focus({ preventScroll: true });
    };
  }, []);

  // Phones: the sheet is modal, lock the page behind it.
  useEffect(() => {
    if (desktop) return;
    const old = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = old;
    };
  }, [desktop]);

  // A newly selected step starts at the top of the panel.
  useEffect(() => {
    scroller.current?.scrollTo({ top: 0 });
  }, [r.n]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key === "Tab" && ref.current) {
      const f = Array.from(
        ref.current.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null);
      if (f.length === 0) return;
      const first = f[0]!;
      const last = f[f.length - 1]!;
      const a = document.activeElement;
      if (e.shiftKey && (a === first || a === ref.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && a === last) {
        e.preventDefault();
        first.focus();
      }
      return;
    }
    const t = e.target as HTMLElement;
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || e.metaKey || e.ctrlKey)
      return;
    if ((e.key === "j" || e.key === "ArrowRight") && next && next !== r.n) {
      e.preventDefault();
      onSelect(next);
    } else if (
      (e.key === "k" || e.key === "ArrowLeft") &&
      prev &&
      prev !== r.n
    ) {
      e.preventDefault();
      onSelect(prev);
    }
  };

  const panel = (
    <>
      {!desktop && (
        <div
          className="fixed inset-0 z-50 bg-black/50 animate-in fade-in-0"
          onClick={onClose}
          data-testid="step-backdrop"
        />
      )}
      <div
        ref={ref}
        role="dialog"
        aria-modal={!desktop}
        aria-label={`Step ${r.n}: ${r.title}`}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        data-testid="step-panel"
        className={cn(
          "fixed z-50 flex flex-col border bg-background shadow-xl outline-none",
          desktop
            ? "inset-y-0 right-0 w-[min(32rem,100vw)] border-l animate-in slide-in-from-right"
            : "inset-x-0 bottom-0 top-8 rounded-t-xl animate-in slide-in-from-bottom",
        )}
      >
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {r.n}
          </span>
          <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">
            {r.title}
          </h2>
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {idx + 1}/{order.length}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={!prev || prev === r.n}
            onClick={() => prev && onSelect(prev)}
            aria-label="Previous step (K)"
            title="Previous step (K / left arrow)"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={!next || next === r.n}
            onClick={() => next && onSelect(next)}
            aria-label="Next step (J)"
            title="Next step (J / right arrow)"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onClose}
            aria-label="Close (Esc)"
            title="Close (Esc)"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div ref={scroller} className="flex-1 overflow-y-auto p-3">
          <StepDetail
            r={r}
            jobs={jobs}
            registryUrl={registryUrl}
            onOpen={onSelect}
            onViewDiagnostics={() => {
              onClose();
              // the panel overlays the drawer: close it, then reveal the section
              setTimeout(() => openBenchDiagnostics(attemptKey), 50);
            }}
          />
        </div>
      </div>
    </>
  );
  return createPortal(panel, document.body);
}

function StripCount({
  n,
  label,
  state,
}: {
  n: number;
  label: string;
  state: StepState;
}) {
  if (n === 0) return null;
  const Icon = STATE_ICON[state];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold",
        PILL[state],
      )}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {n} {label}
    </span>
  );
}

/** The 13 publish steps of an attempt, always all of them (lib/publishSteps.ts)
 *  as collapsed summary rows; a click opens the detail panel (deep-linkable
 *  via #run=<attemptKey>&step=<n>). "by time" shows the true chronology (the
 *  numbering is logical). */
export function PublishSteps({
  input,
  live,
  attemptKey,
}: {
  input: AttemptInput;
  live: boolean;
  /** Identifies the attempt in the URL hash. */
  attemptKey: string;
}) {
  const view = useMemo(() => publishStepsView(input), [input]);
  const jobs = useLiveJobs(
    view ? allBuilds(view.rows).flatMap((b) => (b.jobId ? [b.jobId] : [])) : [],
    live,
  );
  const [byTime, setByTime] = useState(false);
  const [problemsOnly, setProblemsOnly] = useState(false);
  const { step, select } = useStepSelection(attemptKey);
  const desktop = useIsDesktop();
  const registryUrl =
    typeof window === "undefined"
      ? "https://registry.pure.dev"
      : registryPublicUrl(window.location.hostname);
  const all = useMemo(() => (view ? flattenRows(view.rows) : []), [view]);
  const counts = useMemo(() => countStates(view?.rows ?? []), [view]);
  if (!view)
    return (
      <p
        className="text-xs text-muted-foreground"
        data-testid="step-checklist-empty"
      >
        no step detail (this run predates the step checklist)
      </p>
    );
  const byN = new Map(all.map((r) => [r.n, r] as const));
  const chrono = byTime ? chronology(view) : [];
  const shownRows = filterRows(view.rows, problemsOnly);
  const shownChrono = problemsOnly
    ? chrono.filter((e) => {
        const s = byN.get(e.n)?.state;
        return s === "failed" || s === "degraded" || s === "running";
      })
    : chrono;
  const order = (
    byTime
      ? shownChrono.map((e) => e.n)
      : flattenRows(shownRows).map((r) => r.n)
  ).filter((n) => byN.has(n));
  // A deep link / sub-step may point at a row the filter hides: keep it
  // navigable.
  const navOrder = step && !order.includes(step) ? [...order, step] : order;
  const selected = step ? byN.get(step) : undefined;
  return (
    <div className="space-y-2" data-testid="publish-steps">
      <div
        className="flex flex-wrap items-center justify-between gap-2"
        data-testid="step-strip"
      >
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="font-medium">Total elapsed</span>
          <span className="tabular-nums" data-testid="total-elapsed">
            {fmtMs(view.elapsedMs)}
          </span>
          <span className="text-muted-foreground">
            (benchmark included) · {view.summary}
          </span>
          <StripCount n={counts.ok} label="ok" state="ok" />
          <StripCount n={counts.failed} label="failed" state="failed" />
          <StripCount n={counts.degraded} label="degraded" state="degraded" />
          <StripCount n={counts.running} label="running" state="running" />
          <StripCount n={counts.skipped} label="skipped" state="skipped" />
          <StripCount n={counts.pending} label="pending" state="pending" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={problemsOnly}
              onChange={(e) => setProblemsOnly(e.target.checked)}
            />
            failed / degraded only
          </label>
          <div className="inline-flex rounded-md border p-0.5 text-xs">
            {(["logical", "time"] as const).map((m) => (
              <button
                key={m}
                type="button"
                aria-pressed={(m === "time") === byTime}
                onClick={() => setByTime(m === "time")}
                className={cn(
                  "rounded px-2 py-0.5 font-medium",
                  (m === "time") === byTime
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {m === "logical" ? "by step" : "by time"}
              </button>
            ))}
          </div>
        </div>
      </div>
      <ul className="space-y-1">
        {byTime
          ? shownChrono.map((e) => (
              <StepRow
                key={e.n}
                r={byN.get(e.n)!}
                selected={step === e.n}
                onOpen={select}
              />
            ))
          : shownRows.map((r) => (
              <Fragment key={r.n}>
                <StepRow r={r} selected={step === r.n} onOpen={select} />
                {r.subrows.map((s) => (
                  <StepRow
                    key={s.n}
                    r={s}
                    selected={step === s.n}
                    onOpen={select}
                    nested
                  />
                ))}
              </Fragment>
            ))}
      </ul>
      {(problemsOnly ? shownRows.length === 0 : false) && (
        <p className="text-xs text-muted-foreground">
          No failed or degraded steps in this run.
        </p>
      )}
      {selected && (
        <StepPanel
          r={selected}
          order={navOrder}
          desktop={desktop}
          jobs={jobs}
          registryUrl={registryUrl}
          onSelect={select}
          onClose={() => select(null)}
          attemptKey={attemptKey}
        />
      )}
    </div>
  );
}
